import React from 'react';
import CollapsibleInfo from '../components/CollapsibleInfo';

// Updated pricing with early bird offers
const SUBSCRIPTION_PLANS = [
  {
    id: 'basic',
    name: 'Basic Plan',
    originalPrice: '₹999',
    currentPrice: 'Free',
    duration: 'upto FY 2025-26',
    offerText: 'Till 31st April 2026',
    features: [
      'Single User Access',
      'Basic Invoicing', 
      'Inventory Management',
      'Basic Reports',
      'Mobile App Access',
      'GST Reports (View Only)',
      'Email Support'
    ],
    limitations: [
      'Limited to 100 invoices/month',
      'No multi-user access',
      'Basic customer support'
    ],
    recommended: true,
    badgeText: 'Free Till April 2026',
    badgeColor: 'bg-green-500'
  },
  {
    id: 'premium',
    name: 'Premium Plan', 
    originalPrice: '₹3,999',
    currentPrice: '₹999',
    duration: 'per year',
    offerText: 'Early Bird Offer till 31st March 2026',
    features: [
      'Multi-User Access (up to 5)',
      'Advanced Invoicing',
      'GST Reports & Analytics',
      'Advanced Business Reports',
      'Priority Support',
      'Data Backup & Restore',
      'Custom Invoice Templates',
      'Inventory Alerts'
    ],
    limitations: [
      'Limited to 1000 invoices/month'
    ],
    recommended: false,
    badgeText: '75% OFF',
    badgeColor: 'bg-orange-500'
  },
  {
    id: 'enterprise',
    name: 'Enterprise Plan',
    originalPrice: '₹7,999',
    currentPrice: '₹2,999',
    duration: 'per year',
    offerText: 'Early Bird Offer till 31st March 2026',
    features: [
      'Unlimited Users',
      'Custom Workflows',
      'API Access',
      'Advanced Security',
      'Dedicated Support Manager',
      'Custom Reports & Analytics',
      'Multi-Location Support',
      'Advanced User Permissions',
      'Priority Feature Requests'
    ],
    limitations: [],
    recommended: false,
    badgeText: '63% OFF',
    badgeColor: 'bg-purple-500'
  }
];

const PlanCard = ({ plan, selected, onSelect }) => {
  const isBasicPlan = plan.id === 'basic';
  
  return (
    <div 
      className={`relative plan-card cursor-pointer transition-all duration-300 ${
        selected 
          ? 'ring-2 ring-blue-500 shadow-lg transform scale-105' 
          : 'hover:shadow-md hover:transform hover:scale-102'
      } ${plan.recommended ? 'border-2 border-blue-500' : 'border border-gray-200'} 
      rounded-xl p-4 bg-white`}
      onClick={onSelect}
    >
      
      {/* Discount Badge for non-basic plans */}
      {!isBasicPlan && (
        <div className="absolute -top-3 right-4">
          <span className={`${plan.badgeColor} text-white px-3 py-1 rounded-full text-sm font-bold`}>
            {plan.badgeText}
          </span>
        </div>
      )}

      {/* Plan Header */}
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
        
        {/* Pricing */}
        <div className="mb-1">
          {!isBasicPlan && (
            <div className="text-lg text-gray-500 line-through mb-1">
              {plan.originalPrice} per year
            </div>
          )}
          <div className="flex items-center justify-center">
            <span className={`text-3xl font-bold ${isBasicPlan ? 'text-green-600' : 'text-blue-600'}`}>
              {plan.currentPrice}
            </span>
            {plan.duration !== 'upto FY 2025-26' && (
              <span className="text-gray-600 ml-2">/{plan.duration}</span>
            )}
          </div>
        </div>
        
        {/* Offer Text */}
        <div className={`text-sm font-medium ${isBasicPlan ? 'text-green-600' : 'text-orange-600'}`}>
          {plan.offerText}
        </div>
      </div>

      {/* Features */}
      <div className="space-y-3 mb-6">
        {plan.features.map((feature, index) => (
          <div key={index} className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-gray-700">{feature}</span>
          </div>
        ))}
      </div>

      {/* Limitations */}
      {plan.limitations.length > 0 && (
        <div className="border-t pt-4">
          <p className="text-xs text-gray-500 mb-1">Limitations:</p>
          {plan.limitations.map((limitation, index) => (
            <div key={index} className="flex items-start mb-1">
              <svg className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-xs text-gray-500">{limitation}</span>
            </div>
          ))}
        </div>
      )}

      {/* Select Button */}
      <div className="mt-6">
        <button
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
            selected
              ? 'bg-blue-600 text-white'
              : plan.recommended
              ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
          }`}
        >
          {selected ? 'Selected' : 'Select Plan'}
        </button>
      </div>
    </div>
  );
};

const SubscriptionStep = ({ data, onChange }) => {
  const selectedPlan = data.selectedPlan || 'basic';

  const handlePlanSelect = (planId) => {
    onChange({ selectedPlan: planId });
  };

  const handleSkip = () => {
    onChange({ selectedPlan: 'basic' });
  };

  return (
    <div className="subscription-step w-full">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          Choose Your Plan
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Select the plan that best fits your business needs. You can upgrade or downgrade anytime.
        </p>
      </div>

      {/* Plans Grid */}
      <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-4 mb-6">
        {SUBSCRIPTION_PLANS.map(plan => (
          <PlanCard 
            key={plan.id}
            plan={plan}
            selected={selectedPlan === plan.id}
            onSelect={() => handlePlanSelect(plan.id)}
          />
        ))}
      </div>

      {/* Skip Option */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Start with Basic Plan
            </h3>
            <p className="text-gray-600">
              Perfect for getting started. You can upgrade anytime from your company settings.
            </p>
          </div>
          <button
            onClick={handleSkip}
            className="ml-6 px-6 py-3 text-blue-600 hover:text-blue-700 font-medium border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Skip for Now
          </button>
        </div>
      </div>

      {/* Important Notes */}
      <div className="space-y-3">
        {/* Per-Company Billing Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="font-semibold text-yellow-800 mb-1">
                Important: Per-Company Billing
              </h4>
              <p className="text-sm text-yellow-700">
                Each company requires its own subscription. Choose wisely to manage costs effectively. 
                You can manage multiple companies under one account.
              </p>
            </div>
          </div>
        </div>

        {/* Feature Clarifications */}

        {/* Collapsible Info Box */}
        <CollapsibleInfo title="Current Feature Status">
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Basic Plan:</strong> Free till April 2026 - Perfect for small businesses</li>
            <li>• <strong>Premium & Enterprise:</strong> Early bird pricing till March 2026</li>
            <li>• <strong>GST Reports:</strong> Currently view-only, filing feature coming soon</li>
            <li>• <strong>Mobile Access:</strong> All plans include mobile app access and cloud sync</li>
            <li>• <strong>Flexibility:</strong> Upgrade/downgrade anytime without losing data</li>
            <li>• <strong>Skip Option:</strong> You can choose a plan later from settings</li>
            <li>• All plans include core accounting, invoicing, and inventory features</li>
          </ul>
        </CollapsibleInfo>
      </div>
    </div>
  );
};

export default SubscriptionStep;
